import { useEffect, useState } from 'react';
import type { TransferenciaPendiente } from '../../api/ops/equipo';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { EstadoCargando } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import type { NombreIcono } from '../../componentes/iconos';
import { Ventana } from '../../componentes/Ventana';
import type { PropsPantalla } from '../tipos';
import { fechaLarga, nombreDePila, textoDeError } from './textos';
import './equipo.css';

/**
 * Aceptar o rechazar la propiedad del negocio (1.12, quien la recibe; G136): llega desde la campana, el correo o Equipo
 * («Ver la propuesta»). Lee la transferencia de `pending_ownership_transfer` de getBusiness, que solo la ven el
 * propietario y quien la recibe. Al aceptar, el anterior propietario queda como Administrador, además de sus otros roles
 * (G217). Si ya no está pendiente (caducó a los 7 días, G218, se canceló o ya se respondió; 409), lo dice.
 */
type Estado = 'cargando' | 'pendiente' | 'aceptada' | 'rechazada' | 'caducada';

export function AceptarPropiedad({ ops, negocio, membresia, nombreNegocio, navegar, movil, recargar }: PropsPantalla) {
    const avisar = useAvisoBreve();
    const [estado, setEstado] = useState<Estado>('cargando');
    const [transferencia, setTransferencia] = useState<TransferenciaPendiente | null>(null);
    const [ventana, setVentana] = useState(false);
    const [ocupado, setOcupado] = useState<'aceptar' | 'rechazar' | null>(null);
    const yo = membresia?.member_id ?? null;

    useEffect(() => {
        let vivo = true;
        ops.getBusiness(negocio)
            .then((n) => {
                if (!vivo) {
                    return;
                }
                const t = n.pending_ownership_transfer ?? null;
                if (t !== null && t.to_member_id === yo) {
                    setTransferencia(t);
                    setEstado('pendiente');
                } else {
                    setEstado('caducada');
                }
            })
            .catch(() => {
                if (vivo) {
                    setEstado('caducada');
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, yo]);

    const responder = (aceptar: boolean) => {
        if (transferencia === null) {
            return;
        }
        setOcupado(aceptar ? 'aceptar' : 'rechazar');
        (aceptar ? ops.acceptOwnershipTransfer(negocio, transferencia.id) : ops.declineOwnershipTransfer(negocio, transferencia.id))
            .then(() => {
                setVentana(false);
                setEstado(aceptar ? 'aceptada' : 'rechazada');
                // Cambian los permisos y el menú (la persona pasa a ser la propietaria).
                if (aceptar) {
                    recargar();
                }
            })
            .catch((e: unknown) => {
                setVentana(false);
                if (e instanceof ErrorApi && e.estado === 409) {
                    setEstado('caducada');
                    return;
                }
                avisar({ tipo: 'error', texto: textoDeError(e, 'No he podido responder a la propuesta. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(null);
            });
    };

    if (estado === 'cargando') {
        return <EstadoCargando seccion="la propuesta" />;
    }

    const quien = transferencia?.from_member_name ?? '';
    const quienPila = nombreDePila(quien) === '' ? 'Quien te la ofrece' : nombreDePila(quien);
    const caduca = transferencia === null ? '' : fechaLarga(transferencia.expires_at);

    const cabecera: Record<Exclude<Estado, 'cargando'>, [NombreIcono, string, string, string, string]> = {
        pendiente: ['crown', 'var(--rv-red-soft)', 'var(--rv-red)', `${quien} te ofrece la propiedad de ${nombreNegocio}`, `Si aceptas, pasas a ser el propietario del negocio. Tienes hasta ${caduca} para responder; hasta entonces ${quienPila} sigue siéndolo.`],
        // G217: el anterior propietario queda como Administrador, además de sus otros roles.
        aceptada: ['check', 'var(--rv-red)', '#fff', `Ya eres el propietario de ${nombreNegocio}`, `La facturación y el plan están a tu nombre. ${quienPila} queda como Administrador, además de sus otros roles. Revisa el método de pago en tu plan.`],
        rechazada: ['x', 'var(--rv-paper)', 'var(--rv-ink)', 'Has rechazado la propiedad', `${quien} sigue siendo el propietario de ${nombreNegocio} y lo verá en Equipo. Tú sigues en el equipo con tus roles.`],
        caducada: ['clock', 'var(--rv-paper)', 'var(--rv-ink)', 'Esta propuesta ya no está pendiente', 'Ha caducado, la han cancelado o ya la has respondido. Si quieren transferirte la propiedad, tienen que proponerla de nuevo.'],
    };
    const [icono, fondo, tinta, titulo, texto] = cabecera[estado];

    const filas: [NombreIcono, string, string][] = [
        ['crown', 'Pasas a ser el propietario', 'Solo hay uno. Tendrás todo el negocio, también la facturación, la baja y la propiedad.'],
        ['credit-card', 'La facturación pasa a tu nombre', 'El plan y los bloques siguen igual. Desde que aceptes, eres tú quien contrata, paga y recibe las facturas.'],
        ['users', `${quienPila} queda como Administrador`, 'Además de sus otros roles. Puedes cambiarlos después desde Equipo.'],
        ['clock', `Caduca ${caduca}`, `Si no respondes, ${quienPila} sigue siendo el propietario.`],
    ];

    return (
        <div className={`rv-eq-propiedad${movil ? ' rv-eq-propiedad--movil' : ''}`}>
            <span className="rv-eq-propiedad__baldosa" style={{ background: fondo, color: tinta }}>
                <Icono nombre={icono} />
            </span>
            <span className="rv-eq-ceja">Propiedad del negocio</span>
            <h2>{titulo}</h2>
            <p>{texto}</p>
            {estado === 'pendiente' && (
                <>
                    <ul className="rv-eq-propiedad__filas">
                        {filas.map(([i, t, d]) => (
                            <li key={t}>
                                <span className="rv-eq-propiedad__icono">
                                    <Icono nombre={i} />
                                </span>
                                <span>
                                    <strong>{t}</strong>
                                    <span>{d}</span>
                                </span>
                            </li>
                        ))}
                    </ul>
                    <div className="rv-eq-propiedad__botones">
                        <Boton icono="crown" movil={movil} ocupado={ocupado === 'aceptar'} ocupadoTexto="Aceptando…" disabled={ocupado !== null} onClick={() => { responder(true); }}>
                            Aceptar la propiedad
                        </Boton>
                        <Boton tipo="secundario" icono="x" movil={movil} disabled={ocupado !== null} onClick={() => { setVentana(true); }}>
                            Rechazar
                        </Boton>
                    </div>
                </>
            )}
            {estado !== 'pendiente' && (
                <div className="rv-eq-propiedad__botones">
                    <Boton tipo="secundario" icono="users" movil={movil} onClick={() => { navegar('/equipo'); }}>
                        Ver el equipo
                    </Boton>
                </div>
            )}
            <Ventana
                abierta={ventana}
                titulo={`¿Rechazas la propiedad de ${nombreNegocio}?`}
                texto={`${quien} sigue siendo el propietario. Lo verá en Equipo y en Actividad.`}
                accion="Rechazar"
                accionIcono="x"
                ocupado={ocupado === 'rechazar'}
                ocupadoTexto="Rechazando…"
                movil={movil}
                onAccion={() => {
                    responder(false);
                }}
                onCerrar={() => {
                    setVentana(false);
                }}
            />
        </div>
    );
}
